import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { styleSheet } from './styles';

interface PremiumDistributionChartProps {
	data?: {
		title: string;
		visualizationType: string;
		data: {
			id: string;
			label: string;
			count: number;
			value: number;
		}[];
	};
	isLoading?: boolean;
	subtitle?: string;
	className?: string;
}

const formatCountAxisLabel = (value: number): string => {
	if (value >= 1000) {
		const thousands = value / 1000;
		// If it's a whole number, don't show decimals
		if (thousands % 1 === 0) {
			return `${thousands}k`;
		}
		// Otherwise show one decimal place
		return `${thousands.toFixed(1)}k`;
	}
	// For values less than 1000, show as whole number
	return `${Math.round(value)}`;
};

const formatRangeLabel = (label: string): string => {
	// Format premium range labels to use abbreviated notation
	// e.g., "$25,000-$50,000" -> "$25k-$50k", "$100,000+" -> "$100k+"
	return label.replace(/(\d{1,3}),000/g, '$1k');
};

export const PremiumDistributionChart: React.FC<PremiumDistributionChartProps> = ({
	data,
	isLoading,
	subtitle,
	className,
}) => {
	const chartData = React.useMemo<
		{
			id: string;
			label: string;
			formattedLabel: string;
			count: number;
			value: number;
			color: string;
		}[]
	>(() => {
		if (!data?.data || data.data.length === 0) {
			return [];
		}

		// Define order for premium ranges
		const rangeOrder = [
			'$0-$1,000',
			'$1,000-$5,000',
			'$5,000-$10,000',
			'$10,000-$25,000',
			'$25,000-$50,000',
			'$50,000-$100,000',
			'$100,000+',
		];

		// Sort by predefined order and format labels
		const sortedData = [...data.data]
			.map(item => {
				const color = mainChartColor();
				return {
					...item,
					color,
					formattedLabel: formatRangeLabel(item.label),
				};
			})
			.sort((a, b) => {
				const indexA = rangeOrder.indexOf(a.label);
				const indexB = rangeOrder.indexOf(b.label);
				return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
			});

		// Remove all leading entries with count = 0
		const firstNonZeroIndex = sortedData.findIndex(item => item.count > 0);
		if (firstNonZeroIndex === -1) {
			// All entries have count = 0, return empty array
			return [];
		}
		return sortedData.slice(firstNonZeroIndex);
	}, [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No premium distribution data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{tooltipData.label}</p>
					<p className={css(styleSheet.tooltipValue)}>Count: {tooltipData.count}</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title, !!subtitle && styleSheet.titleWithSubtitle)}>
				{data?.title || 'Premium Distribution'}
			</h3>
			{subtitle ? <p className={css(styleSheet.subtitle)}>{subtitle}</p> : null}
			<ResponsiveContainer width='100%' height={400}>
				<BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='formattedLabel'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						angle={-45}
						textAnchor='end'
						height={80}
					/>
					<YAxis
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						tickFormatter={formatCountAxisLabel}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Bar dataKey='count' radius={[4, 4, 0, 0]}>
						{chartData.map(item => {
							return <Cell key={item.id} fill={item.color} />;
						})}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
